// The member Dashboard (/app/dashboard): the numbers (rules panel, stat tiles, charts,
// insight) together with what to do next given the time of day and the
// trader's own journal, their accounts at a glance, recommendations built from their
// trades, this week's numbers, and shortcuts into every part of the app.
import BlownCard from './BlownCard';
import { JourneyHomeCard } from './JourneyOverview';
import { canSeeView } from './AppNav';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { accountStatus } from './balance';
import { POSTS } from './blog/posts';
import { BRIEF_URL, DISCORD_URL } from './config';
import GettingStarted, { readHidden } from './Onboarding';
import { FocusToday } from './Routine';
import { ExperimentLine } from './ExperimentPanel';
import { dayKey, tradingDayKey, currentTradingDay, usd, netPnl } from './pnl';
import { Sparkline } from './Dashboard';
import { performanceStats, unjournaledTrades } from './stats';
import { currentLocale } from './langState';
import { useI18n } from './i18n';
import { Icon } from './Icons';
import { FoundingHomeCard } from './Founding';
import RebillCheck from './RebillCheck';
import { isLocked } from './Plans';
import OpenPositions from './OpenPositions';
import { api } from './api';
import { dueLabel, goalProgress, goalReminderDue } from './Goals';
import { journalStreak } from './journalStreak';

const GOAL_SEEN_KEY = 'mm-goal-checkin-seen';

function readSeen() {
  try {
    return localStorage.getItem(GOAL_SEEN_KEY) || '';
  } catch {
    return '';
  }
}

// Goals with a reminder due today (every day, or on their weekday), until "Got it for today".
// The journaling streak: every day you traded, journaled, in a row, with a nudge when today's
// trades are waiting for their reflection.
function JournalStreak({ trades, notes }) {
  const { t } = useI18n();
  const s = journalStreak(trades, notes);
  if (!s.days) return null;
  const next = s.current + 1;
  return (
    <section className={`home-streak${s.today === 'waiting' ? ' waiting' : ''}`} aria-label={t('Journaling streak')}>
      <span className="home-streak-flame" aria-hidden="true">{s.current ? '🔥' : '📓'}</span>
      <div>
        <strong>{s.current ? t(s.current === 1 ? '1-day journaling streak' : '{n}-day journaling streak', { n: s.current }) : t('No journaling streak yet')}</strong>
        <span className="field-hint">
          {s.today === 'waiting'
            ? t(s.current ? 'You traded today. Write your reflection to make it {n}.' : 'You traded today. Write your reflection to start a streak.', { n: next })
            : s.today === 'done' ? t('Today is journaled.') : s.current ? t('Every day you traded, journaled.') : t('Journal each day you trade to start one.')}
          {s.best > s.current ? ` ${t('Best: {n}.', { n: s.best })}` : ''}
        </span>
      </div>
      {s.today === 'waiting' && <Link to="/app/reflection" className="bt-link-btn">{t('Reflect')} →</Link>}
    </section>
  );
}

function GoalCheckIn({ trades, notes, accounts }) {
  const { t } = useI18n();
  const today = dayKey(new Date());
  const [goals, setGoals] = useState(null);
  const [rules, setRules] = useState([]);
  const [seen, setSeen] = useState(readSeen);

  useEffect(() => {
    let live = true;
    Promise.all([api('/goals'), api('/routine')])
      .then(([g, r]) => {
        if (!live) return;
        setGoals(g.goals || []);
        setRules(r.rules || []);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  if (!goals || seen === today) return null;
  const data = { accounts, trades, notes, rules };
  const due = goals.filter((g) => goalReminderDue(g) && !goalProgress(g, data).done);
  if (!due.length) return null;

  function dismiss() {
    setSeen(today);
    try {
      localStorage.setItem(GOAL_SEEN_KEY, today);
    } catch {
      // Not remembered in private mode; it shows again next time.
    }
  }

  return (
    <section className="home-goals" aria-labelledby="home-goals-title">
      <div className="home-goals-head">
        <div>
          <span className="home-goals-label">{t('Goal check-in')}</span>
          <h2 id="home-goals-title">{t(due.length === 1 ? 'Keep this goal in sight' : 'Keep these goals in sight')}</h2>
        </div>
      </div>
      <ul>
        {due.map((g) => {
          const p = goalProgress(g, data);
          const when = dueLabel(g, false);
          return (
            <li key={g._id}>
              <span className="home-goals-title">
                <span className={`goal-kind ${g.kind}`}>{g.kind === 'process' ? t('Process') : g.kind === 'personal' ? t('Personal') : t('Trading')}</span>
                {g.title}
              </span>
              <div className="status-bar" role="img" aria-label={t('{pct}% complete', { pct: Math.round(p.progress * 100) })}>
                <span className="status-bar-fill goal-fill" style={{ width: `${p.progress * 100}%` }} />
              </div>
              <span className="home-goals-meta">
                <span>{p.label || `${Math.round(p.progress * 100)}%`}</span>
                {when && <span>{when}</span>}
              </span>
              {g.notes && <p className="home-goals-why"><strong>{t('Why it matters:')}</strong> {g.notes}</p>}
            </li>
          );
        })}
      </ul>
      <div className="home-goals-actions">
        <Link to="/app/goals" className="home-link">{t('Open my goals')} &rarr;</Link>
        <button type="button" className="link-button" onClick={dismiss}>{t('Got it for today')}</button>
      </div>
    </section>
  );
}

// Small line icons for the cards.


// The US session in New York time: before the open, during it, after the close, or a weekend.
function sessionPhase(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' })
    .formatToParts(now);
  const get = (type) => parts.find((p) => p.type === type).value;
  if (['Sat', 'Sun'].includes(get('weekday'))) return 'weekend';
  const minutes = Number(get('hour')) * 60 + Number(get('minute'));
  if (minutes < 9 * 60 + 30) return 'before';
  if (minutes < 16 * 60) return 'during';
  return 'after';
}

const prepDone = (n) => Boolean(n && ((n.morningDone && n.morningDone.length) || (n.moodBefore && n.moodBefore.length) || (n.plan && n.plan.trim())));
const reflectionDone = (n) => Boolean(n && (n.score || (n.review && n.review.trim()) || (n.eveningDone && n.eveningDone.length)));

// The one thing to do next.
function nextStep({ phase, trades, accounts, todayNote, todayTrades }) {
  if (!accounts.length && !trades.length) {
    return { icon: 'upload', label: 'Get started', title: 'Set up your first account', text: "Pick your prop firm's preset, then import your trades. Your rules panel fills in from there.", to: '/app', action: 'Set up my journal' };
  }
  if (phase === 'weekend') {
    return { icon: 'chart', label: 'Weekend review', title: 'Review your week', text: "Markets are closed. It's the best time to see what worked, what cost you, and what to change on Monday.", to: '/app/insights', action: 'Open Insights' };
  }
  if (phase === 'before') {
    return prepDone(todayNote)
      ? { icon: 'play', label: 'Before the open', title: "You're prepped. Ready for the session?", text: 'Your plan, rules and reset buttons will be on one calm screen.', to: '/app/session', action: 'Open Live session' }
      : { icon: 'sun', label: 'Before the open', title: 'Start your morning prep', text: "A two-minute check-in, your rules and today's plan, with no P&L on screen.", to: '/app/morning', action: 'Start morning prep' };
  }
  if (phase === 'during') {
    return prepDone(todayNote)
      ? { icon: 'play', label: 'Market open', title: 'Open your Live session', text: 'Keep your rules in front of you, log what you see, and take a timeout when you need one.', to: '/app/session', action: 'Open Live session' }
      : { icon: 'sun', label: 'Market open', title: 'Two minutes of prep before you trade', text: "Check in, read your rules and write today's plan. It's the cheapest risk control there is.", to: '/app/morning', action: 'Start morning prep' };
  }
  if (reflectionDone(todayNote)) {
    return { icon: 'chart', label: 'After the close', title: 'Day reviewed. See your patterns', text: "Your reflection is done. Insights shows what's working and what's costing you.", to: '/app/insights', action: 'Open Insights' };
  }
  return todayTrades.length
    ? { icon: 'moon', label: 'After the close', title: 'Reflect on today', text: 'Score your day, note the emotions that hit, and set one thing to work on tomorrow.', to: '/app/reflection', action: 'Start my reflection' }
    : { icon: 'moon', label: 'After the close', title: "Import today's trades, then reflect", text: 'Your rules panel and insights update with every import.', to: '/app', importFirst: true, action: 'Import trades' };
}

// Up to four suggestions built from the trader's own data.
function recommendations({ trades, notes, plays, library, accounts, today }) {
  const list = [];
  if (!trades.length) {
    list.push({ icon: 'upload', title: 'Import your first trades', text: 'Upload a TopstepX, Tradovate or NinjaTrader export, or any CSV, or add trades by hand.', to: '/app', importFirst: true });
  }
  // The costliest pattern isn't repeated here: the insight card above the list shows it.
  const tradingDays = [...new Set(trades.map((x) => tradingDayKey(x.date)))].filter((d) => d < today).sort();
  const lastDay = tradingDays[tradingDays.length - 1];
  if (lastDay && !reflectionDone(notes[lastDay])) {
    const label = new Date(`${lastDay}T12:00:00`).toLocaleDateString(currentLocale(), { weekday: 'long', month: 'short', day: 'numeric' });
    list.push({ icon: 'moon', title: 'Finish your last reflection', text: label, textVars: true, to: `/app/reflection?date=${lastDay}` });
  }
  const weekAgo = Date.now() - 7 * 86400000;
  const untagged = unjournaledTrades(trades).filter((x) => new Date(x.date).getTime() >= weekAgo);
  if (untagged.length) {
    list.push({ icon: 'tag', title: 'Tag your recent trades', text: untagged.length === 1 ? '{n} trade from the last 7 days has no tag or note.' : '{n} trades from the last 7 days have no tag or note.', n: untagged.length, to: '/app' });
  }
  for (const a of accounts.filter((x) => !x.archived && x.payoutWinningDays && x.size)) {
    const s = accountStatus(a, trades.filter((x) => x.accountId === a._id));
    if (s && s.payoutDays < s.payoutNeeded) {
      list.push({ icon: 'money', title: 'Payout progress', text: '{name}: {have} of {need} winning days for your next payout.', vars: { name: a.name, have: s.payoutDays, need: s.payoutNeeded }, to: '/app' });
    }
  }
  if (trades.length >= 10 && !plays.length) {
    list.push({ icon: 'book', title: 'Build your playbook', text: 'Write down the setups you trade best, with their rules, and see their results.', to: '/app/playbook' });
  }
  return list.slice(0, 4);
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning, {name}' : h < 18 ? 'Good afternoon, {name}' : 'Good evening, {name}';
}

// The daily routine, in order. The rest of the app is one click away in the sidebar.
const SHORTCUTS = [
  { to: '/app/morning', icon: 'sun', title: 'Morning prep', text: 'Check in, read your rules and write your plan before the open.', action: 'Start your day', short: 'Plan, levels and market context.' },
  { to: '/app/session', icon: 'play', title: 'Live session', text: 'Your rules, plan, notes and timeout in front of you while you trade.', action: 'Open the session', short: 'Trade, track and stay within your rules.' },
  { to: '/app/reflection', icon: 'moon', title: 'End-of-day reflection', text: 'Score your day, check your rules and set tomorrow’s focus.', action: 'End your day', short: 'Review, insights and tomorrow’s focus.' },
];

// Whether today's step is done: prep (plan, check-in or checklist), session (any note) or reflection.
function stepStatus(i, note) {
  if (!note) return 'open';
  if (i === 0) return (note.plan && note.plan.trim()) || (note.morningDone && note.morningDone.length) || (note.moodBefore && note.moodBefore.length) ? 'done' : 'open';
  if (i === 1) return note.sessionLog && note.sessionLog.length ? 'done' : 'open';
  return (note.eveningDone && note.eveningDone.length) || note.score || (note.review && note.review.trim()) ? 'done' : 'open';
}

const signed = (n) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${usd.format(Math.abs(n))}`;
const tone = (n) => (n > 0 ? 'gain' : n < 0 ? 'loss' : '');

// One slim row under the greeting: this week and month, then three tiles (P&L, win rate, profit
// factor) that each switch between this year, all time and this month on a click, remembered.
const STRIP_KEY = 'mm-home-strip';
const PERIODS = ['year', 'all', 'month'];
const STRIP_LABELS = {
  pnl: { year: 'Year P&L', all: 'All-time P&L', month: 'Month P&L' },
  wr: { year: 'Year win rate', all: 'All-time win rate', month: 'Month win rate' },
  pf: { year: 'Year profit factor', all: 'All-time profit factor', month: 'Month profit factor' },
};

function NumbersStrip({ trades }) {
  const { t } = useI18n();
  const [periods, setPeriods] = useState(() => {
    try { return { pnl: 'year', wr: 'year', pf: 'year', ...JSON.parse(localStorage.getItem(STRIP_KEY) || '{}') }; } catch { return { pnl: 'year', wr: 'year', pf: 'year' }; }
  });
  if (!trades.length) return null;
  const month = currentTradingDay().slice(0, 7);
  const all = performanceStats(trades);
  const stats = {
    all,
    month: performanceStats(trades.filter((x) => tradingDayKey(x.date).startsWith(month))),
    year: performanceStats(trades.filter((x) => tradingDayKey(x.date).startsWith(month.slice(0, 4)))),
  };
  const next = (key) => setPeriods((was) => {
    const p = { ...was, [key]: PERIODS[(PERIODS.indexOf(was[key]) + 1) % PERIODS.length] };
    try { localStorage.setItem(STRIP_KEY, JSON.stringify(p)); } catch { /* kept for this visit only */ }
    return p;
  });
  const p = stats[periods.pnl] || stats.year;
  const w = stats[periods.wr] || stats.year;
  const f = stats[periods.pf] || stats.year;
  const pf = f.profitFactor === null ? '—' : f.profitFactor === Infinity ? '∞' : f.profitFactor.toFixed(2);
  const label = (key) => t(STRIP_LABELS[key][periods[key]] || STRIP_LABELS[key].year);
  const items = [
    ['calendar', t('Week P&L'), signed(all.week), tone(all.week)],
    ['calendar', t('Month P&L'), signed(stats.month.net), tone(stats.month.net)],
    ['chart', label('pnl'), signed(p.net), tone(p.net), undefined, 'pnl'],
    ['target', label('wr'), w.winRate === null ? '—' : `${Math.round(w.winRate)}%`, '', w.winRate, 'wr'],
    ['insights', label('pf'), pf, f.profitFactor === null ? '' : f.profitFactor >= 1 ? 'gain' : 'loss', undefined, 'pf'],
  ];
  return (
    <ul className="home-strip" aria-label={t('Your numbers')}>
      {items.map(([icon, text, value, cls, ring, key]) => {
        const inner = (
          <>
            <span className="home-strip-icon" aria-hidden="true"><Icon name={icon} /></span>
            <span className="home-strip-body">
              <span className="home-strip-label">{text}</span>
              <strong className={cls}>{value}</strong>
            </span>
            {ring !== undefined && ring !== null && (
              <svg className="home-strip-ring" viewBox="0 0 36 36" aria-hidden="true">
                <circle cx="18" cy="18" r="15" />
                <circle cx="18" cy="18" r="15" className="on" strokeDasharray={`${(ring / 100) * 94.2} 94.2`} />
              </svg>
            )}
          </>
        );
        return (
          <li key={icon + (key || text)}>
            {key ? (
              <button type="button" className="home-strip-switch" onClick={() => next(key)} title={t('Click to change the period')}>
                {inner}
                <span className="home-strip-cycle" aria-hidden="true">⇄</span>
              </button>
            ) : inner}
          </li>
        );
      })}
    </ul>
  );
}

const HOME_ACCOUNT_KEY = 'mm-home-account';

// One account at a glance, for every kind of account: balance and growth, the last trading day,
// the last 30 days, win rate and the balance line. Prop accounts add their rules: target, max loss left, daily loss
// left and progress to the target.
function AccountOverview({ accounts, trades }) {
  const { t } = useI18n();
  // Prop accounts (with a target or a max loss) first, since that's where the numbers matter most.
  const open = accounts.filter((a) => !a.archived && a.size)
    .sort((a, b) => Number(Boolean(b.profitTarget || b.maxLossLimit)) - Number(Boolean(a.profitTarget || a.maxLossLimit)));
  const [picked, setPicked] = useState(() => {
    try {
      return localStorage.getItem(HOME_ACCOUNT_KEY) || '';
    } catch {
      return '';
    }
  });
  if (!open.length) return null;
  const account = open.find((a) => a._id === picked) || open[0];
  const mine = trades.filter((x) => x.accountId === account._id);
  const s = accountStatus(account, mine);
  if (!s) return null;
  const pick = (id) => {
    setPicked(id);
    try {
      localStorage.setItem(HOME_ACCOUNT_KEY, id);
    } catch {
      // Not remembered in private mode.
    }
  };
  // The last day this account traded, and the last 30 days.
  const byDay = {};
  for (const x of mine) byDay[tradingDayKey(x.date)] = (byDay[tradingDayKey(x.date)] || 0) + netPnl(x);
  const days = Object.keys(byDay).sort();
  const lastDay = days[days.length - 1];
  const lastCount = mine.filter((x) => tradingDayKey(x.date) === lastDay).length;
  const since = dayKey(new Date(Date.now() - 30 * 86400000));
  const recent = mine.filter((x) => tradingDayKey(x.date) > since);
  const recentNet = recent.reduce((sum, x) => sum + netPnl(x), 0);
  const recentWins = recent.filter((x) => netPnl(x) > 0).length;
  const dayName = (key) => new Date(`${key}T12:00:00`).toLocaleDateString(currentLocale(), { month: 'long', day: 'numeric' });
  const growth = s.start ? (s.change / s.start) * 100 : 0;
  const signed = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${usd.format(Math.abs(v))}`;
  const tone = (v) => (v > 0 ? 'gain' : v < 0 ? 'loss' : '');
  // The balance from trades, day by day (the line's zero is the starting balance).
  let run = 0;
  const curve = [0, ...days.map((k) => (run += byDay[k]))].slice(-90);
  const ruled = Boolean(account.profitTarget || account.maxLossLimit);
  const badge = s.risk === 'blown' ? ['loss', t('Blown')] : s.overall === 'danger' ? ['loss', t('At risk')] : s.overall === 'warning' ? ['warn', t('Careful')] : ['gain', t('Active')];
  const tiles = [
    [t('Account balance'), usd.format(s.balance), '', mine.length ? t('{amount} from trading ({pct})', { amount: signed(s.change), pct: `${growth >= 0 ? '+' : '−'}${Math.abs(growth).toFixed(1)}%` }) : t('No trades yet')],
    [t('Last trading day'), lastDay ? signed(byDay[lastDay]) : '—', lastDay ? tone(byDay[lastDay]) : '', lastDay ? `${dayName(lastDay)} · ${t(lastCount === 1 ? '{n} trade' : '{n} trades', { n: lastCount })}` : ''],
    [t('Last 30 days'), signed(recentNet), tone(recentNet), t(recent.length === 1 ? '{n} trade' : '{n} trades', { n: recent.length })],
    [t('Win rate, last 30 days'), recent.length ? `${Math.round((recentWins / recent.length) * 100)}%` : '—', '', recent.length ? t('{w} wins · {l} losses', { w: recentWins, l: recent.length - recentWins }) : ''],
  ];
  const rules = [
    ...(s.target ? [[t('Profit target'), usd.format(s.target), '', s.toPass > 0 ? t('{amount} to go', { amount: usd.format(s.toPass) }) : t('Reached')]] : []),
    ...(s.floor !== undefined ? [[t('Max loss left'), usd.format(Math.max(0, s.toBlow)), s.risk === 'safe' ? '' : 'loss']] : []),
    ...(s.dailyLeft !== undefined ? [[t('Daily loss left'), usd.format(Math.max(0, s.dailyLeft)), s.dailyRisk === 'safe' ? '' : 'loss']] : []),
  ];
  const tile = ([label, value, cls, note]) => (
    <div key={label} className="home-account-tile">
      <span className="home-strip-label">{label}</span>
      <strong className={cls}>{value}</strong>
      {note && <span className="home-account-note">{note}</span>}
    </div>
  );
  return (
    <section className="home-account" aria-labelledby="home-account-title">
      <div className="home-heading-row">
        <h2 className="home-heading" id="home-account-title">{t('Account overview')}</h2>
        <Link to="/app/accounts" className="home-link">{t('Open accounts')} &rarr;</Link>
      </div>
      <div className="home-account-pick">
        {open.length > 1 ? (
          <select value={account._id} onChange={(e) => pick(e.target.value)} aria-label={t('Account')}>
            {open.map((a) => <option key={a._id} value={a._id}>{a.name}</option>)}
          </select>
        ) : <strong>{account.name}</strong>}
        <span className={`home-account-badge ${badge[0]}`}>{badge[1]}</span>
      </div>
      <div className="home-account-tiles">{tiles.map(tile)}</div>
      {curve.length > 2 && (
        <div className="home-account-curve" title={t('Balance from trading, day by day')}>
          <Sparkline values={curve} wide />
        </div>
      )}
      {ruled && (rules.length > 0 || s.progress !== undefined) && (
      <div className="home-account-tiles home-account-rules">
        {rules.map(tile)}
        {s.progress !== undefined && (
          <div className="home-account-tile home-account-progress">
            <span className="home-strip-label">{t('Progress')}</span>
            <strong className="gain">{Math.round(s.progress * 100)}%</strong>
            <span className="home-account-bar" aria-hidden="true"><span style={{ width: `${Math.round(s.progress * 100)}%` }} /></span>
          </div>
        )}
      </div>
      )}
    </section>
  );
}

// Ask MARK from Home: a question box and a few starters that open Coach MARK and ask him there.
const ASK_CHIPS = ['How did I do today?', 'What’s my biggest leak right now?', 'Did I follow my rules this week?', 'What should I work on in my next session?'];
function AskMark() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const ask = (q) => { if (q.trim()) navigate('/app/mark', { state: { ask: q.trim() } }); };
  return (
    <section className="home-ask">
      <form className="home-ask-box" onSubmit={(e) => { e.preventDefault(); ask(text); }}>
        <span className="home-ask-spark" aria-hidden="true">✦</span>
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={400} placeholder={t('Ask MARK about your trading…')} aria-label={t('Ask MARK about your trading…')} />
        <button type="submit" className="small" disabled={!text.trim()}>{t('Ask MARK')} &rarr;</button>
      </form>
      <div className="home-ask-chips">
        {ASK_CHIPS.map((q) => <button key={q} type="button" className="home-ask-chip" onClick={() => ask(t(q))}>{t(q)}</button>)}
      </div>
    </section>
  );
}

// Which step of the routine fits the time of day: before the open, during it, after it.
function routineStep(now = new Date()) {
  const minutes = now.getHours() * 60 + now.getMinutes();
  return minutes < 9 * 60 + 30 ? 0 : minutes < 16 * 60 ? 1 : 2;
}

function Home({ user, trades, notes, accounts, plays, library, loaded, experiments, onImport, onOnboarding, onAccountChange, onSessionExpired }) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const today = dayKey(new Date());
  const todayNote = notes[today];
  const todayTrades = trades.filter((x) => tradingDayKey(x.date) === currentTradingDay());
  const phase = sessionPhase();
  const ready = loaded.trades && loaded.notes;
  const next = ready ? nextStep({ phase, trades, accounts, todayNote, todayTrades }) : null;
  const recs = ready ? recommendations({ trades, notes, plays, library, accounts, today }) : [];
  // A brand-new member sees the getting-started steps first, instead of a separate "Next up" card.
  const firstVisit = ready && loaded.plays && !user.isDemo && !trades.length && !readHidden(user);
  const latest = POSTS[0];
  const local = (path) => `${lang === 'es' ? '/es' : ''}${path}`;
  const dateLabel = new Date().toLocaleDateString(currentLocale(), { weekday: 'long', month: 'long', day: 'numeric' });

  function go(item) {
    if (item.importFirst) onImport();
    else navigate(item.to);
  }

  const openChat = () => window.dispatchEvent(new CustomEvent('mm-open-chat'));

  return (
    <div className="home">
      <div className="page-title home-title">
        <p className="eyebrow">{dateLabel}</p>
        <h1>{t(greeting(), { name: user.name.split(' ')[0] })}</h1>
      </div>

      {ready && !firstVisit && <NumbersStrip trades={trades} />}

      {/* Members who have Coach MARK can ask him straight from Home. */}
      {ready && !firstVisit && !user.isDemo && canSeeView(user, 'mark') && !isLocked(user) && <AskMark />}

      {!user.isDemo && <FoundingHomeCard user={user} />}

      {firstVisit && (
        <GettingStarted user={user} accounts={accounts} trades={trades} notes={notes} plays={plays} onAction={onOnboarding} />
      )}

      {/* Two columns: what to do next and the day's work on the left; the focus from the last
          reflection, goals, open positions and the journey on the right. */}
      <div className="home-cols">
        <div className="home-main">
          {next && !firstVisit && (
            <button type="button" className={`home-next phase-${phase}`} onClick={() => go(next)}>
              <span className="home-next-icon"><Icon name={next.icon} /></span>
              <span className="home-next-body">
                <span className="home-next-label">{t(next.label)}</span>
                <span className="home-next-title">{t(next.title)}</span>
                <span className="home-next-text">{t(next.text)}</span>
              </span>
              <span className="home-next-action">{t(next.action)} &rarr;</span>
            </button>
          )}

          {/* Recommended and today's plan side by side, right under what to do next. */}
          <div className="home-pair">
            <section className="home-section">
              <h2 className="home-heading">{t('Recommended for you')}</h2>
              {recs.length ? (
                <ul className="home-list">
                  {recs.map((r) => (
                    <li key={r.title}>
                      <button type="button" className={`home-row${r.tone ? ` tone-${r.tone}` : ''}`} onClick={() => go(r)}>
                        <span className="home-row-icon"><Icon name={r.icon} /></span>
                        <span className="home-row-body">
                          <strong>{t(r.title)}</strong>
                          <span>{r.textVars || r.tone ? r.text : t(r.text, { n: r.n, ...(r.vars || {}) })}</span>
                        </span>
                        <span className="home-row-arrow" aria-hidden="true">&rarr;</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="home-empty">{ready ? t("You're all caught up. Nice work.") : t('Loading…')}</p>
              )}
            </section>
            <section className="home-section home-plan">
              <h2 className="home-heading">{t("Today's plan")}</h2>
              <div className="home-plan-box">
                {todayNote && todayNote.plan ? <p className="sidebar-plan">{todayNote.plan}</p> : <p className="sidebar-muted">{t('No plan written for today yet.')}</p>}
                <Link to="/app/morning" className="home-link">{todayNote && todayNote.plan ? t('Edit plan') : t("Write today's plan")} &rarr;</Link>
              </div>
            </section>
          </div>

          {ready && !firstVisit && !user.isDemo && !isLocked(user) && canSeeView(user, 'journey') && (
            <RebillCheck user={user} accounts={accounts} onAccountChange={onAccountChange} onSessionExpired={onSessionExpired} />
          )}

          <section className="home-section home-day-box">
            <h2 className="home-heading">{t('Your day')}</h2>
            <ul className="home-day home-day-compact">
              {SHORTCUTS.map((step, i) => {
                const status = stepStatus(i, todayNote);
                const now = i === routineStep() && status !== 'done';
                return (
                  <li key={step.to}>
                    <Link to={step.to} className={`home-day-card${now ? ' is-now' : ''}${status === 'done' ? ' is-done' : ''}`}>
                      <span className="home-day-icon"><Icon name={step.icon} /></span>
                      <strong>{t(step.title)}</strong>
                      <span>{i === 0 && todayNote && todayNote.plan ? t('Plan written ✓') : t(step.short)}</span>
                      <span className={`home-day-status status-${status === 'done' ? 'done' : now ? 'now' : 'later'}`}>
                        {status === 'done' ? t('Completed') : now ? t('Now') : t(i < routineStep() ? 'Not done' : 'Later')}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>

          {ready && !firstVisit && <BlownCard user={user} accounts={accounts} trades={trades} notes={notes} onAccountChange={onAccountChange} onSessionExpired={onSessionExpired} />}
          {ready && !firstVisit && <AccountOverview accounts={accounts} trades={trades} />}


          {!firstVisit && !user.isDemo && loaded.trades && loaded.notes && loaded.plays && (
            <GettingStarted user={user} accounts={accounts} trades={trades} notes={notes} plays={plays} onAction={onOnboarding} />
          )}
        </div>

        <aside className="home-aside">
          <FocusToday notes={notes} date={today} checklist />
          {ready && !firstVisit && <JournalStreak trades={trades} notes={notes} />}
          {ready && !firstVisit && <GoalCheckIn trades={trades} notes={notes} accounts={accounts} />}
          {ready && !firstVisit && trades.length > 0 && canSeeView(user, 'journey') && <JourneyHomeCard accounts={accounts} trades={trades} />}
          {!firstVisit && <OpenPositions user={user} accounts={accounts} />}
          <ExperimentLine experiments={experiments} />
        </aside>
      </div>

      {/* Resources: one slim row of links, since they aren't needed every day. */}
      <nav className="home-links" aria-label={t('Resources')}>
        {latest && (
          <Link to={local(`/blog/${latest.slug}`)} title={(latest[lang] || latest.en).title}><Icon name="blog" /> {t('Blog')}</Link>
        )}
        {BRIEF_URL && <a href={BRIEF_URL} target="_blank" rel="noopener noreferrer"><Icon name="sun" /> {t('Pre-market brief')}</a>}
        <Link to={local('/tools')}><Icon name="calc" /> {t('Free calculators')}</Link>
        {DISCORD_URL && <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer"><Icon name="discord" /> {t('Community')}</a>}
        <button type="button" onClick={openChat}><Icon name="chat" /> {t('Help')}</button>
      </nav>
    </div>
  );
}

export default Home;
